import { ChatMessageGetModel } from "api";
import { compareDays, dateTimeToView } from "helpers/date";
import uniqBy from "lodash/uniqBy";
import { STEP } from "sources/messages";

export function concatMessages({
    prevData = [],
    items,
    nextData = [],
}: {
    prevData?: ChatMessageGetModel[];
    items: ChatMessageGetModel[];
    nextData?: ChatMessageGetModel[];
}) {
    const fullData = [...prevData, ...items.filter((item) => item.createdAt), ...nextData];
    const formattedData: Array<ChatMessageGetModel | { msgType: "date"; text: string }> = [];
    fullData.reduce((result, current) => {
        if (compareDays(result, current.createdAt)) {
            const text = dateTimeToView(current.createdAt, "Do MMM YYYY");
            formattedData.push({ msgType: "date", text });
            formattedData.push(current);
            return current.createdAt;
        }
        formattedData.push(current);
        return result;
    }, "");
    return formattedData;
}

export function calcChatDates(items: Array<ChatMessageGetModel | { msgType: "date"; text: string }>) {
    return items
        .filter((item) => "msgType" in item && item.msgType === "date")
        .map((item, i, arr) => {
            const next = arr[i + 1];
            const prev = arr[i - 1];
            return { ...item, i, next, prev };
        });
}

export function preformatMessagesParams(items: ChatMessageGetModel[]) {
    const firstMsg = items.find((item) => item.createdAt);
    if (firstMsg) {
        const createdAtDate = new Date(firstMsg.createdAt);
        return { id: { ne: firstMsg.id }, createdAt: { lt: createdAtDate } };
    } else {
        return {};
    }
}

export function handleGetMessagesRes({
    res,
    items,
    first,
}: {
    res: ChatMessageGetModel[];
    items: ChatMessageGetModel[];
    first: boolean;
}) {
    const prevData = [...res].reverse();
    const nextItems = concatMessages({ items: first ? [] : items, prevData });
    const dates = calcChatDates(nextItems);
    const result = {
        items: nextItems,
        dates,
        shouldUpdate: true,
        endReached: res.length !== (first ? STEP * 2 : STEP),
    };
    return result;
}

export function applyMsgNextState(
    {
        state,
        contentHeight,
        visibleHeight,
    }: { state: { scrollPos: number }; contentHeight: number; visibleHeight: number },
    chatState: { items: ChatMessageGetModel[]; newMsgCount: number; skip: number },
    data: ChatMessageGetModel,
) {
    const isBottom = state.scrollPos >= contentHeight - visibleHeight;
    const { items, newMsgCount, skip } = chatState;
    const nextItems = concatMessages({ items, nextData: [data] });
    const nextState: {
        items: Array<ChatMessageGetModel | { msgType: "date"; text: string }>;
        dates: Array<ChatMessageGetModel | { msgType: "date"; text: string }>;
        skip: number;
        newMsgCount?: number;
    } = {
        items: uniqBy(nextItems, (item) => ("id" in item ? item.id : (item as { text: string }).text)),
        dates: calcChatDates(nextItems),
        skip: skip + 1,
    };
    if (!isBottom) {
        nextState.newMsgCount = newMsgCount + 1;
    }
    return nextState;
}
